/* eslint-disable prefer-template */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from '../../utils/axios';

export const API = 'bank-statement';
export const name = 'bankStatementSlice';

export const emptyItem = () => ({
  date: null,
  description: '',
  inflow: 0,
  outflow: 0,
});

export const sampleStatement = {
  id: 1,
  statement_number: '12',
  bank_name: 'Banca Intesa',
  account_number: '160-1234567890123-45',
  statement_date: '2026-09-15',
  previous_balance: 150000,
  items: [
    { date: '2026-09-02', description: 'Uplata održavanja', inflow: 12000, outflow: 0 },
    { date: '2026-09-08', description: 'Račun za struju', inflow: 0, outflow: 8500 },
  ],
};

export const emptyStatement = () => ({
  statement_number: '',
  bank_name: '',
  account_number: '',
  statement_date: null,
  previous_balance: 0,
  items: [emptyItem()],
});

interface InitState {
  isLoading: boolean,
  isSaving: boolean,
  show: boolean,
  id: number | null,
  items: Array<any>,
  details: any,
}

function NewReducer() {
  const initialState: InitState = {
    isLoading: false,
    isSaving: false,
    show: false,
    id: null,
    items: [sampleStatement],
    details: null,
  };

  const reducers = {
    resetSlice: () => initialState,
    setShow: (state: InitState, action: PayloadAction<{ show: boolean, id: number | null }>) => {
      state.show = action.payload.show;
      state.id = action.payload.id;
      if (!action.payload.show) {
        state.details = null;
      }
    },
    startRead: (state: InitState) => {
      state.isLoading = true;
    },
    finishRead: (state: InitState, action: PayloadAction<any>) => {
      state.items = action.payload || [];
      state.isLoading = false;
    },
    startDetails: (state: InitState) => {
      state.isLoading = true;
    },
    finishDetails: (state: InitState, action: PayloadAction<any>) => {
      state.details = action.payload;
      state.isLoading = false;
    },
    startSave: (state: InitState) => {
      state.isSaving = true;
    },
    finishSave: (state: InitState) => {
      state.isSaving = false;
    },
  };

  const slice = createSlice({
    name,
    initialState,
    reducers,
  });

  const { actions } = slice;

  const readRows = (payload: any) => {
    if (Array.isArray(payload)) {
      return payload;
    }

    if (payload && Array.isArray(payload.data)) {
      return payload.data;
    }

    return [];
  };

  const apis = {
    callReadApi: (callback: (state: boolean, data: any, message: string) => void) => async (dispatch: any) => {
      dispatch(actions.startRead());

      await axios.get(API).then((result: any) => {
        const { data, message } = result.data;
        const rows = readRows(data);
        const withSample = rows.some((row: any) => row.id === sampleStatement.id) ? rows : [sampleStatement, ...rows];

        callback(true, withSample, message);
        dispatch(actions.finishRead(withSample));
      }).catch((error: any) => {
        const { message } = error;

        callback(false, null, message);
        dispatch(actions.finishRead([sampleStatement]));
      });
    },

    callDetailsApi: (id: number | string, callback: (state: boolean, data: any, message: string) => void) => async (dispatch: any) => {
      if (Number(id) === sampleStatement.id) {
        callback(true, sampleStatement, '');
        dispatch(actions.finishDetails(sampleStatement));
        return;
      }

      dispatch(actions.startDetails());

      await axios.get(API + '/' + id).then((result: any) => {
        const { data, message } = result.data;

        callback(true, data, message);
        dispatch(actions.finishDetails(data));
      }).catch((error: any) => {
        const { message } = error;

        callback(false, null, message);
        dispatch(actions.finishDetails(null));
      });
    },

    callSaveApi: (params: any, callback: (state: boolean, data: any, message: string) => void) => async (dispatch: any, getState: any) => {
      dispatch(actions.startSave());

      const { id } = getState()[name];
      const request = id ? axios.put(API + '/' + id, params) : axios.post(API, params);

      await request.then((result: any) => {
        const { data, message } = result.data;

        callback(true, data, message);
        dispatch(actions.finishSave());
      }).catch((error: any) => {
        const { message } = error;

        callback(false, null, message);
        dispatch(actions.finishSave());
      });
    },
  };

  return {
    reducer: slice.reducer,
    ...actions,
    ...apis,
  };
}

export default NewReducer();
